import { useAuth } from 'common-react/auth/AuthProvider';
import { DYNATRACE_ACTIONS } from 'constants/dynatrace-actions';
import Cookies from 'js-cookie';
import debounce from 'lodash/debounce';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { useStore } from 'store';
import { delay } from 'utils/delay';

export const COOKIE_KEY_PREFIX = 'pluto';
export const COOKIE_KEY_COMPANY_ID = `${COOKIE_KEY_PREFIX}_active_company_id`;
export const COOKIE_KEY_SITE_ID = `${COOKIE_KEY_PREFIX}_active_site_id`;
export const COOKIE_KEY_CURRENT_DATE = `${COOKIE_KEY_PREFIX}_active_date`;
export const COOKIE_KEY_USER_ID = `${COOKIE_KEY_PREFIX}_user_id`;
export const COOKIE_KEY_USER_FIRST_NAME = `${COOKIE_KEY_PREFIX}_user_first_name`;
export const COOKIE_KEY_USER_LAST_NAME = `${COOKIE_KEY_PREFIX}_user_last_name`;
export const COOKIE_KEY_USER_ROLES = `${COOKIE_KEY_PREFIX}_user_roles`;
const SEND_DELAY = 100;
const DEBOUNCE_DELAY = 300;

declare global {
  interface Window {
    dtrum?: {
      identifyUser?: (userId: string) => void;
      enterAction?: (actionName: string) => number;
      leaveAction?: (actionId: number) => void;
    };
  }
}

export function useDynatraceSessionTrackingContext() {
  const { authStoreAccess } = useAuth();
  const jwtToken = authStoreAccess.useJwtToken();

  useEffect(() => {
    _setSessionContext(jwtToken);
  }, [jwtToken]);
}

export function useDynatraceActionTrackingContext() {
  const { company, site } = useParams();
  const [currentDate] = useStore((state) => [state.currentDate]);
  useEffect(() => {
    _setActionContext(company, site, currentDate);
  }, [company, site, currentDate]);
}

export const useDynatraceTriggerAction = (action: DYNATRACE_ACTIONS) => {
  useEffect(() => {
    dynatraceTrackInstantAction(action);
  }, []);
};

export function dynatraceTrackInstantAction(actionName: string) {
  const $promise = _triggerInstantAction(actionName);
  if ($promise) {
    $promise.then();
  }
}

export const _triggerInstantAction = debounce(async function (
  actionName: string,
) {
  await delay(SEND_DELAY);
  if (
    window.dtrum &&
    typeof window.dtrum.enterAction === 'function' &&
    typeof window.dtrum.leaveAction === 'function'
  ) {
    const actionId = window.dtrum.enterAction(actionName);
    window.dtrum.leaveAction(actionId);
  }
}, DEBOUNCE_DELAY);

export function dynatraceTrackStartAction(actionName: string) {
  _startAction(actionName).then();
}

export function dynatraceTrackEndAction(actionName: string) {
  _endAction(actionName).then();
}

export function _setSessionContext(jwtToken?: string) {
  if (jwtToken && jwtToken !== 'pending') {
    const isValidJwtToken = jwtToken.split('.').length === 3;
    if (!isValidJwtToken) {
      return;
    }

    const payload = atob(jwtToken.split('.')[1]);
    const data = JSON.parse(payload) as {
      appClaims: {
        appUserId: number;
        appUserSourceKey: string;
        roles: string[];
        permissions: string[];
        authorizedCompanySourceKeys: string[];
      };
    };

    const appUserId = data.appClaims?.appUserId;
    const roles = data.appClaims?.roles ?? [];

    Cookies.set(COOKIE_KEY_USER_ID, appUserId?.toString());
    Cookies.set(COOKIE_KEY_USER_ROLES, roles.join(','));

    if (appUserId) {
      _identifyUser(appUserId);
    }
  }
}

export function _identifyUser(userId: number) {
  // Make sure identifyUser is available globally
  if (window.dtrum && typeof window.dtrum.identifyUser === 'function') {
    window.dtrum.identifyUser(userId.toString());
  }
}

export function _setActionContext(
  company?: string,
  site?: string,
  currentDate?: Date,
) {
  if (company) {
    Cookies.set(COOKIE_KEY_COMPANY_ID, company.toString());
  }
  if (site) {
    Cookies.set(COOKIE_KEY_SITE_ID, site.toString());
  }
  if (currentDate) {
    Cookies.set(COOKIE_KEY_CURRENT_DATE, currentDate.toISOString());
  }
}

export async function _startAction(actionName: string) {
  await delay(SEND_DELAY);
  // Add a delay to ensure cookies have been updated with latest values
  if (window.dtrum && typeof window.dtrum.enterAction === 'function') {
    const actionId = window.dtrum.enterAction(actionName);
    Cookies.set(`${COOKIE_KEY_PREFIX}-${actionName}-id`, actionId.toString());
  }
}

export async function _endAction(actionName: string) {
  await delay(SEND_DELAY);
  // Add a delay to ensure cookies have been updated with latest values
  if (window.dtrum && typeof window.dtrum.leaveAction === 'function') {
    const actionIdStr = Cookies.get(`${COOKIE_KEY_PREFIX}-${actionName}-id`);
    Cookies.remove(`${COOKIE_KEY_PREFIX}-${actionName}-id`);
    if (actionIdStr) {
      window.dtrum.leaveAction(parseInt(actionIdStr, 10));
    }
  }
}
